/**
 * Mapbox Directions API Integration
 *
 * Unlike the Optimization API (which reorders stops to find the shortest trip),
 * the Directions API computes the real driving route for the stops *in the order
 * given*. We use it to honor a dispatcher's manual stop order while still
 * refreshing the real road distances, durations, and map geometry.
 *
 * Falls back to a local haversine estimate on error or when exceeding the
 * waypoint limit. The fallback ALSO preserves the given order (it never
 * reorders) — that is the whole point of "recalculate along my order".
 *
 * API Docs: https://docs.mapbox.com/api/navigation/directions/
 */

import { env } from '@/config/environment';
import { RouteStop } from '@/types/technicianRoutes';
import { calculateDistance } from './routeOptimization';

// =============================================================================
// Types
// =============================================================================

interface MapboxDirectionsLeg {
  distance: number; // meters
  duration: number; // seconds
}

interface MapboxDirectionsRoute {
  distance: number; // meters
  duration: number; // seconds
  geometry?: { type: 'LineString'; coordinates: [number, number][] };
  legs?: MapboxDirectionsLeg[];
}

interface MapboxDirectionsResponse {
  code: string; // 'Ok' on success
  routes?: MapboxDirectionsRoute[];
  message?: string;
}

export interface RouteGeometry {
  type: 'LineString';
  coordinates: [number, number][]; // [lng, lat] pairs
}

export interface DirectionsResult {
  /** Stops in the SAME order they were passed, with refreshed leg metrics + stop_order. */
  stops: RouteStop[];
  totalDistanceMiles: number;
  totalDurationMinutes: number;
  geometry?: RouteGeometry;
  source: 'mapbox_directions' | 'haversine';
}

// =============================================================================
// Constants
// =============================================================================

const METERS_TO_MILES = 0.000621371;
const SECONDS_TO_MINUTES = 1 / 60;

// Mapbox Directions allows 25 coordinates per request (driving profile).
// One of those is the start location, so 24 stops max.
const MAX_COORDINATES = 25;
const MAX_STOPS = MAX_COORDINATES - 1;

const MAPBOX_DIRECTIONS_URL = 'https://api.mapbox.com/directions/v5/mapbox/driving';

// =============================================================================
// Fallback
// =============================================================================

/**
 * Local haversine fallback. Keeps the given order, fills leg metrics with
 * straight-line distance (and a 2 min/mile estimate), and synthesizes a
 * straight-line geometry through the stops.
 */
function fallbackToHaversine(
  stops: RouteStop[],
  startLocation: { lat: number; lng: number },
  reason: string
): DirectionsResult {
  // eslint-disable-next-line no-console
  console.log(`[MapboxDirections] Falling back to haversine: ${reason}`);

  let totalMiles = 0;
  let prevLat = startLocation.lat;
  let prevLng = startLocation.lng;

  const recalced: RouteStop[] = stops.map((stop, idx) => {
    const miles = calculateDistance(prevLat, prevLng, stop.lat, stop.lng);
    totalMiles += miles;
    prevLat = stop.lat;
    prevLng = stop.lng;
    return {
      ...stop,
      stop_order: idx + 1,
      distance_from_previous_miles: miles,
      duration_from_previous_minutes: Math.round(miles * 2)
    };
  });

  const geometry: RouteGeometry = {
    type: 'LineString',
    coordinates: [
      [startLocation.lng, startLocation.lat],
      ...stops.map(s => [s.lng, s.lat] as [number, number])
    ]
  };

  return {
    stops: recalced,
    totalDistanceMiles: totalMiles,
    totalDurationMinutes: recalced.reduce((s, st) => s + st.duration_from_previous_minutes, 0),
    geometry,
    source: 'haversine'
  };
}

// =============================================================================
// Main Function
// =============================================================================

/**
 * Compute the real driving route for `stops` in the ORDER GIVEN (no reordering),
 * returning refreshed per-leg metrics, totals, and map geometry.
 *
 * @param stops - Route stops in the desired (e.g. user-chosen) order
 * @param startLocation - Starting location (technician's base)
 */
export async function recalcRouteAlongOrder(
  stops: RouteStop[],
  startLocation: { lat: number; lng: number }
): Promise<DirectionsResult> {
  if (stops.length === 0) {
    return {
      stops: [],
      totalDistanceMiles: 0,
      totalDurationMinutes: 0,
      source: 'mapbox_directions'
    };
  }

  const token = env.routing.mapboxAccessToken;
  if (!token) {
    return fallbackToHaversine(stops, startLocation, 'No Mapbox API token configured');
  }

  if (stops.length > MAX_STOPS) {
    return fallbackToHaversine(
      stops,
      startLocation,
      `Too many stops (${stops.length} > ${MAX_STOPS} max)`
    );
  }

  // Build coordinates string: lng,lat;lng,lat;... — start first, stops in order.
  const coordinates = [
    `${startLocation.lng},${startLocation.lat}`,
    ...stops.map(s => `${s.lng},${s.lat}`)
  ].join(';');

  const url =
    `${MAPBOX_DIRECTIONS_URL}/${coordinates}` +
    `?geometries=geojson&overview=full&annotations=distance,duration&access_token=${token}`;

  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Mapbox API HTTP error: ${response.status} ${response.statusText}`);
    }

    const data: MapboxDirectionsResponse = await response.json();

    if (data.code !== 'Ok') {
      throw new Error(`Mapbox API error: ${data.code} - ${data.message || 'Unknown error'}`);
    }

    const route = data.routes?.[0];
    if (!route) {
      throw new Error('Mapbox API returned no route');
    }

    const totalDistanceMiles = route.distance * METERS_TO_MILES;
    const totalDurationMinutes = route.duration * SECONDS_TO_MINUTES;

    // legs[i] is the segment from waypoint i to waypoint i+1. Waypoint 0 is the
    // start, so legs[i] is the segment *into* stops[i].
    const legs = route.legs || [];
    const recalced: RouteStop[] = stops.map((stop, idx) => {
      const leg = legs[idx];
      return {
        ...stop,
        stop_order: idx + 1,
        // miles stay fractional (NUMERIC column); minutes MUST be whole — the
        // upsert RPC casts duration_from_previous_minutes with ::int.
        distance_from_previous_miles: leg ? leg.distance * METERS_TO_MILES : 0,
        duration_from_previous_minutes: leg ? Math.round(leg.duration * SECONDS_TO_MINUTES) : 0
      };
    });

    return {
      stops: recalced,
      totalDistanceMiles,
      totalDurationMinutes,
      ...(route.geometry && { geometry: route.geometry }),
      source: 'mapbox_directions'
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    // eslint-disable-next-line no-console
    console.error('[MapboxDirections] API error:', errorMessage);

    return fallbackToHaversine(stops, startLocation, errorMessage);
  }
}
